import { PortalTelemetryEvent } from './portalTelemetryEvents';
import { PortalTelemetryScreen } from './portalTelemetryScreens';
import { useEffect, useRef } from 'react';
import { currentPortalContext, PortalContext, trackPortalEvent } from './portalTelemetry';

/** Observe screens/steps once per transition; deduplicate Strict Mode effect replay. */
export function usePortalScreen(context: PortalContext | undefined, step?: string, ready = true, mode?: 'new' | 'existing' | 'clone') {
  const last = useRef('');
  const feature = context?.feature;
  const screen = context?.screen;
  useEffect(() => {
    if (!feature || !ready) return;
    const key = JSON.stringify([feature, screen, step, mode]);
    if (last.current === key) return;
    last.current = key;
    trackPortalEvent({ feature, screen }, step ? PortalTelemetryEvent.StepViewed : PortalTelemetryEvent.ScreenViewed, { step, mode });
  }, [feature, screen, step, ready, mode]);
}

/** Full paths are used only locally to detect navigation, never as telemetry properties. */
export function usePortalRoute(pathname: string, search: string) {
  const last = useRef('');
  useEffect(() => {
    const key = pathname + search;
    if (last.current === key) return;
    last.current = key;
    const context = currentPortalContext();
    if (!context) return;
    trackPortalEvent(context, PortalTelemetryEvent.RouteViewed);
    const query = new URLSearchParams(search);
    if (/^\/(payment-success|donation-payment-success)(\/|$)/i.test(pathname)) {
      const code = query.get('Rc');
      trackPortalEvent(context, PortalTelemetryEvent.PaymentReturn, { returnState: code === 'C' ? 'cancelled' : code ? 'success_screen' : 'unknown' });
    } else if (query.get('cancelPayment') === 'true') {
      trackPortalEvent(context, PortalTelemetryEvent.PaymentReturn, { returnState: 'cancelled' });
    }
  }, [pathname, search]);
}

export function usePortalAccessBlocked() {
  const emitted = useRef(false);
  useEffect(() => {
    if (emitted.current) return;
    emitted.current = true;
    trackPortalEvent(currentPortalContext(), PortalTelemetryEvent.AccessBlocked, { screen: PortalTelemetryScreen.Unauthorized });
  }, []);
}
